import React from 'react';
import {
  Box,
  InputBase,
  Button,
  IconButton,
  Tooltip,
  Typography,
  Stack,
  Chip,
  alpha,
  useTheme,
} from '@mui/material';
import { Search, Filter, X, Trash2 } from 'lucide-react';

export default function FilterBar({
  searchTerm = '',
  onSearchChange,
  searchPlaceholder = 'Search...',
  selectedCount = 0,
  onBulkDelete,
  onOpenFilter,
  filterCount = 0,
  onResetFilters,
  hasActiveFilters = false,
  activeFilterBadges = [], // Array of { key, label, onRemove }
  children,
  sx = {},
}) {
  const theme = useTheme();

  return (
    <Box
      sx={{
        p: { xs: 1.75, sm: 2 },
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        alignItems: { xs: 'stretch', sm: 'center' },
        justifyContent: 'space-between',
        gap: 1.5,
        borderBottom: `1px solid ${theme.palette.divider}`,
        bgcolor: selectedCount > 0 ? alpha(theme.palette.primary.main, 0.04) : 'transparent',
        transition: 'background-color 0.2s ease',
        ...sx,
      }}
    >
      {selectedCount > 0 ? (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'primary.main' }}>
            {selectedCount} selected
          </Typography>
          {onBulkDelete && (
            <Tooltip title="Delete selected">
              <Button
                variant="outlined"
                color="error"
                size="small"
                startIcon={<Trash2 size={16} />}
                onClick={onBulkDelete}
                sx={{ borderRadius: 1.5 }}
              >
                Delete Selected
              </Button>
            </Tooltip>
          )}
        </Box>
      ) : (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1, minWidth: 0, flexWrap: 'wrap' }}>
            {onSearchChange && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  bgcolor: theme.palette.background.paper,
                  borderRadius: 1.5,
                  px: 1.25,
                  py: 0.6,
                  border: `1px solid ${theme.palette.divider}`,
                  width: { xs: '100%', sm: 300, md: 340 },
                  transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
                  '&:focus-within': {
                    borderColor: 'primary.main',
                    boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.1)}`,
                  },
                }}
              >
                <Search size={18} color={theme.palette.text.secondary} />
                <InputBase
                  value={searchTerm}
                  onChange={(e) => onSearchChange(e.target.value)}
                  placeholder={searchPlaceholder}
                  sx={{
                    ml: 1,
                    flex: 1,
                    fontSize: '0.875rem',
                    color: 'text.primary',
                  }}
                />
                {searchTerm && (
                  <IconButton
                    size="small"
                    onClick={() => onSearchChange('')}
                    aria-label="Clear search"
                    sx={{ p: 0.25, color: 'text.disabled' }}
                  >
                    <X size={15} />
                  </IconButton>
                )}
              </Box>
            )}

            {activeFilterBadges && activeFilterBadges.length > 0 && (
              <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ display: { xs: 'none', md: 'flex' } }}>
                {activeFilterBadges.map((badge) => (
                  <Chip
                    key={badge.key || badge.label}
                    label={badge.label}
                    size="small"
                    onDelete={badge.onRemove}
                    sx={{
                      bgcolor: alpha(theme.palette.primary.main, 0.08),
                      color: 'primary.main',
                      borderColor: alpha(theme.palette.primary.main, 0.2),
                      border: '1px solid',
                      fontSize: '0.75rem',
                    }}
                  />
                ))}
              </Stack>
            )}
          </Box>

          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            justifyContent={{ xs: 'space-between', sm: 'flex-end' }}
          >
            {hasActiveFilters && onResetFilters && (
              <Button
                size="small"
                onClick={onResetFilters}
                sx={{
                  color: 'error.main',
                  fontSize: '0.8125rem',
                  fontWeight: 600,
                  textTransform: 'none',
                  px: 1,
                }}
              >
                Reset
              </Button>
            )}

            {onOpenFilter && (
              <Button
                variant={filterCount > 0 ? 'contained' : 'outlined'}
                color={filterCount > 0 ? 'primary' : 'inherit'}
                size="small"
                onClick={onOpenFilter}
                startIcon={<Filter size={16} />}
                sx={{
                  borderRadius: 1.5,
                  fontSize: '0.8125rem',
                  fontWeight: 600,
                  borderColor: theme.palette.divider,
                }}
              >
                Filters {filterCount > 0 ? `(${filterCount})` : ''}
              </Button>
            )}

            {children}
          </Stack>
        </>
      )}
    </Box>
  );
}
